import React, { useMemo } from "react";
import { Row, Col, Button } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import MenuCard from "./MenuCard";
// Hotfix 11 — drill-down header now uses the shared DashboardPageHeader
// for a consistent banner aesthetic across the app (was cramped inline
// Button + title side-by-side).
import DashboardPageHeader from "./DashboardKit/DashboardPageHeader";
import useAuth from "../../pages/Personal Attendance/useAuth";
import { TOOLTRACKER_URL, openExternalApp } from "../../helpers/externalApps";

/**
 * MenuHub — workflow launcher with 2-level drill-down. Renders on the
 * /workflows route (linked from the sidebar's "General SS White" item).
 *
 *   Level 1 ("home"):   all workflow cards (Time/Attendance, Task Mgmt,
 *                       EPO, etc.)
 *   Level 2 ("zoom"):   for workflows that have sub-actions (Personal
 *                       Attendance Change, Travel Expense), drilling in
 *                       shows those sub-actions as sub-cards.
 *
 * Workflows WITHOUT sub-actions navigate directly to their `to:` path
 * on click. Workflows WITH sub-actions drill in instead — and any
 * sub-action then navigates to its `to:` path.
 *
 * The catalog lives at the top of this file. Edit there to add/rename.
 */

// ─────────────────────────────────────────────────────────────────────
// Workflow catalog. `subActions` is optional.
// If present, clicking the card drills in instead of navigating.
//
// PERMISSIONS — each card can specify ONE of:
//   • module: "epo.accounting"      Hidden unless useAuth().canAccessModule(...) is true.
//                                    Keys defined in EffectiveAccessService.cs.
//   • requiresManagerOrAdmin: true  Shortcut for isManager || isItAdmin || isHR || isSuperUser.
//
// Cards without a permission gate are visible to everyone.
// If a workflow's ALL sub-actions are hidden, the workflow card flips to
// direct-navigate mode (clicking it goes to wf.to instead of drilling in).
// ─────────────────────────────────────────────────────────────────────
export const WORKFLOWS = [
    {
        key: "time", group: "daily", label: "Time and Attendance", subtitle: "Clock in / out, view reports",
        icon: "ri-time-line", tone: "info", to: "/time-and-attendance",
        module: "time.open",   // Round 2: same key as the route (card was ungated)
    },
    {
        key: "tasks", group: "daily", label: "Task Management", subtitle: "Plan, assign, monitor",
        icon: "ri-task-line", tone: "success", to: "/task-management",
        module: "tasks.open",  // Round 2: true for everyone until enforcement is switched on
    },
    {
        key: "epo", group: "approvals", label: "Expense Purchase Order", subtitle: "Submit / approve EPOs",
        icon: "ri-money-dollar-circle-line", tone: "warning", to: "/epo",
        subActions: [
            { key: "newepo", label: "Create New EPO", subtitle: "Submit a new expense purchase order", icon: "ri-add-circle-line", tone: "primary", to: "/epo", module: "epo.create" },
            { key: "myepos", label: "My EPOs", subtitle: "EPOs I've created", icon: "ri-folder-line", tone: "info", to: "/epo", module: "epo.mine" },
            { key: "subepos", label: "Subordinate EPOs", subtitle: "EPOs created by my reports", icon: "ri-team-line", tone: "secondary", to: "/epo/subordinates", module: "epo.subordinates" },
            { key: "myEpoApprovals", label: "EPOs Awaiting My Approval", subtitle: "EPOs I need to approve or reject", icon: "ri-check-double-line", tone: "warning", to: "/epo/awaiting-my-approval", module: "epo.myApprovals" },
            { key: "mpcviewer", label: "MPC Viewer", subtitle: "Multi-purchase consolidated view", icon: "ri-eye-line", tone: "info", to: "/epo/mpc-viewer", module: "epo.mpc" },
            // R30 — Accounting EPO Viewer. Same data as MPC Viewer, gated
            // to epo.accounting so Babu/Finance users see "last 1000 EPOs
            // across the company" without needing the epo.mpc module.
            { key: "acctviewer", label: "Accounting EPO Viewer", subtitle: "Last 1000 EPOs across the company", icon: "ri-eye-line", tone: "info", to: "/epo/accounting-viewer", module: "epo.accounting" },
            { key: "notready", label: "Not Ready for Payment", subtitle: "EPOs not yet approved for payment", icon: "ri-time-line", tone: "warning", to: "/epo/not-ready-for-payment", module: "epo.notReady" },
            { key: "readypay", label: "Ready for Payment", subtitle: "Approved EPOs awaiting payment", icon: "ri-bank-card-line", tone: "success", to: "/epo/ready-for-payment", module: "epo.readyPay" },
            { key: "shipping", label: "Shipping", subtitle: "EPOs awaiting receipt / shipment", icon: "ri-truck-line", tone: "info", to: "/epo/shipping", module: "epo.receive" },
            // Batch 3 / Round 8 — No-EPO / Missing-EPO shipment tracker. Shown
            // to users granted "EPO - Missing-EPO Shipments" (or IT-Admin).
            { key: "missingepo", label: "Missing-EPO Shipments", subtitle: "Packages received without an EPO", icon: "ri-mail-send-line", tone: "warning", to: "/epo/missing-epo", module: "epo.missingEpo" },
            { key: "findepo", label: "Find EPO", subtitle: "Search past EPOs", icon: "ri-search-line", tone: "secondary", to: "/epo/find", module: "epo.findByNumber" },
            // Batch 3 / Round 7 — find an EPO by line-item description (Shipping aid).
            { key: "findbyitem", label: "Find EPO by Item", subtitle: "Search line items to locate an EPO", icon: "ri-search-eye-line", tone: "secondary", to: "/epo/find-by-item", module: "epo.lineSearch" },
            // Owner decision (2026-09-24): India EPOs (SN vote / signatory) are approved in the
            // separate India EPO dashboard and are out of scope here; their tables are not in the
            // Azure database, so the "SN EPOs Awaiting Approval" tile is no longer offered (2026-10-02).
        ],
    },
    {
        key: "cr", group: "quality", label: "Contract Review", subtitle: "New CRs and pending reviews",
        icon: "ri-file-text-line", tone: "primary", to: "/contract-review",
        module: "cr.open",     // Round 2: same key as the route (card was ungated)
    },
    {
        key: "project", group: "approvals", label: "Project Approval System", subtitle: "Submit / approve projects",
        icon: "ri-folder-chart-line", tone: "info", to: "/projects/awaiting-approval",
        subActions: [
            { key: "deptprojects", label: "View My Department's Projects", subtitle: "Departmental view", icon: "ri-team-line", tone: "secondary", to: "/projects/my-department", module: "projects.deptProjects" },
            { key: "awaiting", label: "View Projects Awaiting Approval", subtitle: "Pending your approval", icon: "ri-time-line", tone: "warning", to: "/projects/awaiting-approval", module: "projects.awaiting" },
        ],
    },
    {
        // ── Round HR-PA (Parts 16–19): the employee's attendance drill-down
        // is now SIX plain-language cards (the nine-item mixed list read as
        // an internal tool). Terminology is spelled out — "Flexible Time
        // Off", "Change of Attendance" — and the supervisor-only views sit
        // under a separate "My Team" divider so a regular employee never
        // sees manager tooling mixed into their personal list. The two
        // calendar views were folded into "My Schedule" (one calendar, an
        // FTO/COA toggle lives on the page itself); every old route still
        // works via allRoutes.js.
        key: "pac", group: "daily", label: "Personal Attendance", subtitle: "Time off and schedule-change requests",
        icon: "ri-calendar-event-line", tone: "secondary", to: "/attendance",
        subActions: [
            { key: "myattendance", label: "My Attendance", subtitle: "Everything in one place — requests and status", icon: "ri-calendar-2-line", tone: "primary", to: "/attendance", module: "attendance.mine" },
            { key: "newfto", label: "Request Time Off", subtitle: "Flexible Time Off (FTO) — vacation, sick, personal", icon: "ri-add-circle-line", tone: "primary", to: "/attendance/fto/new", module: "attendance.newFto" },
            { key: "newcoa", label: "Request Schedule Change", subtitle: "Change of Attendance (COA) — arrive late / leave early", icon: "ri-time-line", tone: "info", to: "/attendance/coa/new", module: "attendance.newCoa" },
            { key: "mybalance", label: "My FTO Balance", subtitle: "Earned, used, available", icon: "ri-wallet-3-line", tone: "success", to: "/attendance/my-balance", module: "attendance.myBalance" },
            { key: "myfto", label: "My Schedule", subtitle: "My time off on a calendar", icon: "ri-calendar-check-line", tone: "primary", to: "/attendance/fto/schedule", module: "attendance.myFtoSched" },
            { key: "ytdfto", label: "My Requests & YTD", subtitle: "History and year-to-date status", icon: "ri-bar-chart-line", tone: "info", to: "/attendance/ytd", module: "attendance.ytdFto" },
            // ── My Team (supervisors / HR / IT only — hidden for everyone else)
            { key: "teamdivider", section: "My Team", divider: true, requiresManagerOrAdmin: true },
            { key: "approvals", label: "Waiting for My Decision", subtitle: "Team requests to approve or deny", icon: "ri-check-double-line", tone: "warning", to: "/attendance/approvals", requiresManagerOrAdmin: true },
            { key: "deptfto", label: "Team Time Off", subtitle: "My department's FTO", icon: "ri-team-line", tone: "secondary", to: "/attendance/dept/fto", module: "attendance.deptFto" },
            { key: "deptcoa", label: "Team Schedule Changes", subtitle: "My department's COA", icon: "ri-team-line", tone: "secondary", to: "/attendance/dept/coa", module: "attendance.deptCoa" },
        ],
    },
    {
        key: "travel", group: "approvals", label: "Travel Expense Approval", subtitle: "Trip submissions and approvals",
        icon: "ri-flight-takeoff-line", tone: "warning", to: "/travel-expense",
        subActions: [
            { key: "newtravel", label: "Submit New Request", subtitle: "New travel expense submission", icon: "ri-add-circle-line", tone: "primary", to: "/travel-expense/new", module: "travel.new" },
            { key: "mytravel", label: "My Requests", subtitle: "Past submissions", icon: "ri-folder-line", tone: "info", to: "/travel-expense", module: "travel.mine" },
            { key: "appqueue", label: "Approval Queue", subtitle: "Trips waiting on your approval", icon: "ri-check-double-line", tone: "warning", to: "/travel-expense/approvals", module: "travelExpense.approval" },  // Go-live parity (2026-09-24): travel #31 opens on the approval tab
            // Go-live parity (2026-09-24): legacy TEA back-office queues.
            { key: "travelhr", label: "Awaiting Prepurchase", subtitle: "HR: pre-arrangement requests to book", icon: "ri-hotel-line", tone: "info", to: "/travel-expense/hr-arrangements", module: "travel.hr" },
            { key: "travelacct", label: "Awaiting Reimbursement", subtitle: "Accounting: submitted trip expenses, overdue reports", icon: "ri-bank-card-line", tone: "success", to: "/travel-expense/accounting", module: "travel.accounting" },
        ],
    },
    {
        key: "dms", group: "quality", label: "Document Management", subtitle: "Docs, ECNs, controlled documents",
        icon: "ri-folder-2-line", tone: "dark", to: "/document-management",
        // ════════════════════════════════════════════════════════════════════
        // Hotfix 14 — DMS sub-actions for the drill-down view.
        //
        // Before this fix the DMS workflow card had `to: "/document-management"`
        // but no subActions, so clicking it skipped straight to the doc list
        // without showing sub-cards (the user remembered them and noticed they
        // were "removed by mistake"). Now drilling in surfaces the actual
        // DMS routes that exist in the modern FE:
        //
        //   • All Documents              → /document-management (visible to all)
        //   • My Drafts                  → /dms/my-drafts        (R12 — visible to all)
        //   • My Pending Approvals       → /dms/my-pending-approvals (visible to all)
        //   • Documents Ready for Release → /dms/awaiting-release
        //                                  (gated via module: "dms.release",
        //                                   same permission set the
        //                                   route's RoleProtected uses)
        //
        // The release card auto-hides for users without dms.release perm
        // (RoleId 4/8 or "Documents Ready for Release"), matching the
        // route-level gate added in R1/R2. Keeping one access path with
        // consistent gating across MenuHub + sidebar + direct URL.
        // ════════════════════════════════════════════════════════════════════
        subActions: [
            { key: "alldocs", label: "All Documents", subtitle: "Browse and search all docs", icon: "ri-folder-open-line", tone: "primary", to: "/document-management", module: "dms.all" },
            { key: "mydrafts", label: "My Drafts", subtitle: "Saved but not yet submitted", icon: "ri-draft-line", tone: "secondary", to: "/dms/my-drafts", module: "dms.myDrafts" },
            { key: "myapprovals", label: "My Pending Approvals", subtitle: "Docs awaiting your action", icon: "ri-time-line", tone: "warning", to: "/dms/my-pending-approvals", module: "dms.myApprovals" },
            { key: "release", label: "Documents Ready for Release", subtitle: "Approved docs awaiting release", icon: "ri-send-plane-line", tone: "success", to: "/dms/awaiting-release", module: "dms.release" },
        ],
    },
    {
        key: "training", group: "daily", label: "My Training", subtitle: "Required and completed training",
        // Round 15: ri-graduation-cap-line is not in the bundled RemixIcon
        // font (rendered as an empty tile — owner screenshot). Swapped to
        // ri-book-open-line which is guaranteed present.
        icon: "ri-book-open-line", tone: "success", to: "/dms/my-training",
        module: "training.open",   // Round 2: same key as the route (card was ungated)
    },
    // Owner 2026-10-08: the Organizational Chart is an App now (see "apps" below),
    // open to every signed-in account; the stand-alone card is gone.
    {
        key: "di", group: "quality", label: "Digital Inspection", subtitle: "Open the DI workspace",
        icon: "ri-checkbox-circle-line", tone: "success", to: "/di", module: "app.di",
    },
    {
        key: "display", group: "company", label: "View Display Board", subtitle: "Live shop-floor status",
        icon: "ri-computer-line", tone: "info", to: "/display-boards", module: "app.displayboard",
    },
    // Support module (Freshdesk). Gated by the server-resolved "support"
    // module key (open to all authenticated users — everyone can create and
    // view their own tickets; agent/admin scope is enforced server-side).
    {
        key: "support", group: "company", label: "Support", subtitle: "Freshdesk tickets",
        icon: "ri-customer-service-2-line", tone: "primary", to: "/support", module: "support",
    },
    // Apps — every app from the sidebar's Apps dropdown, as sub-cards (owner
    // request). Same module gates as the sidebar; `external` entries open in
    // a new tab (Tool Tracker is a separate application).
    {
        key: "apps", group: "company", label: "Apps", subtitle: "All apps in one place",
        icon: "ri-apps-2-line", tone: "primary", to: "/workflows?wf=apps",
        subActions: [
            { key: "app-di", label: "Digital Inspection", subtitle: "Open the DI workspace", icon: "ri-checkbox-circle-line", tone: "success", to: "/di", module: "app.di" },
            { key: "app-perflexion", label: "Perflexion", subtitle: "Part formulas, properties & dimensions", icon: "ri-compasses-2-line", tone: "danger", to: "/perflexion", module: "app.perflexion" },
            { key: "app-mafia", label: "Mafia Aux", subtitle: "Inspection specs, gages & parts", icon: "ri-key-2-line", tone: "dark", to: "/mafia", module: "app.mafia" },
            { key: "app-display", label: "Display Board", subtitle: "Live shop-floor status", icon: "ri-computer-line", tone: "info", to: "/display-boards", module: "app.displayboard" },
            { key: "app-visitors", label: "Expected Visitors", subtitle: "Visitor register & check-in", icon: "ri-user-received-2-line", tone: "warning", to: "/visitors", module: "app.visitors" },
            { key: "app-orgchart", label: "Organizational Chart", subtitle: "Reporting structure", icon: "ri-organization-chart", tone: "primary", to: "/organizational-chart", module: "orgchart.open" },
            { key: "app-support", label: "Support", subtitle: "Freshdesk tickets", icon: "ri-customer-service-2-line", tone: "primary", to: "/support", module: "support" },
            { key: "app-tooltracker", label: "Tool Tracker", subtitle: "Opens in a new tab", icon: "ri-hammer-line", tone: "info", to: TOOLTRACKER_URL, external: true, module: "app.tooltracker" },
        ],
    },
    // Go-live parity (2026-09-24): India accounts — HR gives the people of Epicor
    // company SN a dashboard login. HR gate ("dept.hr" = HR or IT Admin), like the route.
    {
        key: "india-employees", group: "company", label: "India Employees", subtitle: "HR: dashboard accounts for India",
        icon: "ri-global-line", tone: "secondary", to: "/hr/india-employees", module: "dept.hr",
    },
    // Round 20 (2026): Admin moved out of the Workflows launcher — it now
    // lives in the sidebar's IT Admin section at /admin. Keeping admin
    // tools separate so the main launcher stays the same for everyone.
];

// ─────────────────────────────────────────────────────────────────────

// Round 15 — grouped launcher sections (owner feedback + dashboard design
// research: group related tools into labeled sections separated by white
// space, keep one consistent grid). Order here controls render order.
const GROUPS = [
    { key: "daily", label: "Daily essentials" },
    { key: "approvals", label: "Approvals & spending" },
    { key: "quality", label: "Quality & documents" },
    { key: "company", label: "Company" },
];

// Owner (2026-10-06): the launcher page shows ONLY the time modules (clock in and out,
// FTO and COA); the sidebar entry is named after them. The rest of the catalog stays:
// the department pages open those modules' hub pages (/workflows?wf=<key>) and read
// their sub-actions from here.
export const HUB_NAME = "Time, FTO & COA";
const LANDING_KEYS = ["time", "pac"];

export default function MenuHub({ onItemClick }) {
    const navigate = useNavigate();
    // Round 20 — the drill-down is driven by the URL (?wf=<key>) instead of
    // component state, so every workflow hub has a real, linkable address
    // (/workflows?wf=travel). Breadcrumbs on module sub-pages point here,
    // the browser back button works through hubs, and refresh keeps you
    // in the hub you were viewing.
    const [searchParams, setSearchParams] = useSearchParams();
    const wfParam = searchParams.get("wf");
    const openWorkflow = (key) => setSearchParams(key ? { wf: key } : {});

    // Permission flags. The "manager" check means: this person has direct
    // reports (the backend sets isManager when the user appears as a
    // manager in the employee hierarchy). IT Admins see everything.
    const auth = useAuth();
    const { isManager, isItAdmin, isHR, isSuperUser, canAccessModule } = auth;
    const canSeeManagerCards = !!(isManager || isItAdmin || isSuperUser || isHR);

    // Returns true if `entry` should be visible to the current user.
    //   • If entry.module is set, defer to backend's module gate
    //   • Else if entry.requiresManagerOrAdmin, check that
    //   • Else visible to everyone
    const isVisible = (entry) => {
        if (entry.module) return canAccessModule(entry.module);
        if (entry.requiresManagerOrAdmin) return canSeeManagerCards;
        return true;
    };

    const filterSubActions = (subActions) => {
        if (!subActions) return subActions;
        return subActions.filter(isVisible);
    };

    // A workflow is shown when:
    //   • it has no gate itself (visible to everyone), OR its module check passes
    //   • AND if it has sub-actions, AT LEAST ONE of them is also visible to the user
    //     (otherwise drilling in shows an empty grid, which is confusing)
    const isWorkflowVisible = (wf) => {
        if (!isVisible(wf)) return false;
        if (wf.subActions && wf.subActions.length > 0) {
            // Workflow itself is visible — but if user can't see ANY of its
            // sub-actions, hide the workflow card entirely.
            return wf.subActions.some(isVisible);
        }
        return true;
    };

    // Derived from the URL — single source of truth. Permission-checked the
    // same way the card grid is, so a pasted ?wf= link can't reveal a hub
    // the user can't see (it just falls back to the grid).
    const active = useMemo(() => {
        if (!wfParam) return null;
        const wf = WORKFLOWS.find((w) => w.key === wfParam);
        if (!wf || !isVisible(wf)) return null;
        const subs = filterSubActions(wf.subActions);
        if (!subs || subs.length === 0) return null;
        return { ...wf, subActions: subs };
    }, [wfParam, isManager, isItAdmin, isHR, isSuperUser]);

    const handleWorkflowClick = (wf) => {
        if (wf.subActions && wf.subActions.length > 0) {
            openWorkflow(wf.key);                // drill in (linkable URL)
        } else {
            if (onItemClick) onItemClick({ to: wf.to, label: wf.label, icon: wf.icon });
            navigate(wf.to);
        }
    };

    const handleSubActionClick = (sa) => {
        if (sa.external) { openExternalApp(sa.to); return; }   // separate app → new tab
        if (onItemClick) onItemClick({ to: sa.to, label: sa.label, icon: sa.icon });
        navigate(sa.to);
    };

    // ── Drill-down view: a single workflow's sub-actions ────────────
    if (active) {
        const visible = filterSubActions(active.subActions) || [];
        // A time module goes back to this page; any other module was opened from a
        // department page, the home page or the search, so Back returns there.
        const onLanding = LANDING_KEYS.includes(active.key);
        const goBack = () => (onLanding ? openWorkflow(null) : navigate(-1));
        return (
            <div>
                {/* Hotfix 11 — banner-style header (DashboardPageHeader)
                    replaces the cramped inline "Back to workflows + title"
                    layout. The back button becomes a right-side action,
                    giving the page the same polished look as DMS pages
                    (MyPendingApprovals, etc.). */}
                <DashboardPageHeader
                    icon={active.icon}
                    title={active.label}
                    subtitle={active.subtitle}
                    tone="default"
                    compact={true}
                    breadcrumb={onLanding
                        ? [{ label: HUB_NAME, onClick: () => openWorkflow(null) }, { label: active.label }]
                        : [{ label: active.label }]}
                    actions={
                        <Button
                            color="light"
                            size="sm"
                            onClick={goBack}
                            className="d-inline-flex align-items-center gap-1"
                        >
                            <i className="ri-arrow-left-line"></i>
                            {onLanding ? `Back to ${HUB_NAME}` : "Back"}
                        </Button>
                    }
                />

                <Row className="g-3">
                    {visible.map((sa) => (
                        // Round HR-PA (Part 16): `divider` entries render a
                        // full-width labeled separator (e.g. "My Team" in
                        // Personal Attendance) — same isVisible gating, so a
                        // non-supervisor never sees the heading either.
                        sa.divider ? (
                            <Col xs={12} key={sa.key} className="pb-0">
                                <div className="d-flex align-items-center gap-2 mt-2">
                                    <span className="text-muted small fw-semibold text-uppercase" style={{ letterSpacing: '.04em' }}>
                                        {sa.section}
                                    </span>
                                    <div className="flex-grow-1 border-top" />
                                </div>
                            </Col>
                        ) : (
                            <Col xxl={3} xl={4} md={6} sm={6} key={sa.key}>
                                <div style={{ height: "100%" }}>
                                    <MenuCard
                                        icon={sa.icon}
                                        label={sa.label}
                                        subtitle={sa.subtitle}
                                        tone={sa.tone}
                                        onClick={() => handleSubActionClick(sa)}
                                    />
                                </div>
                            </Col>
                        )
                    ))}
                </Row>
            </div>
        );
    }

    // ── Default view: all workflow cards ─────────────────────────────
    return (
        <div>
            {/* Owner (2026-10-06): the same banner as the department pages. */}
            <DashboardPageHeader
                icon="ri-time-line"
                eyebrow="Attendance"
                title={HUB_NAME}
                subtitle="Clock in and out, time off and schedule changes"
                tone="blue"
                breadcrumb={[
                    { label: "Dashboard", to: "/" },
                    { label: HUB_NAME },
                ]}
            />

            {GROUPS.map((g) => {
                // Owner (2026-10-06): only the time modules are listed on this page, no group label.
                const items = WORKFLOWS.filter((wf) => wf.group === g.key && LANDING_KEYS.includes(wf.key)).filter(isWorkflowVisible);
                if (items.length === 0) return null;
                return (
                    <div key={g.key} className="mb-4">
                        <Row className="g-3">
                            {items.map((wf) => {
                                // Count only sub-actions this user can see — if a workflow
                                // has 13 sub-actions but the user can only see 11, show "11".
                                // If filtering reduces them to 0 we treat the workflow as
                                // having no sub-actions (clicking just navigates).
                                const visibleSubs = filterSubActions(wf.subActions);
                                const hasSubs = visibleSubs && visibleSubs.length > 0;
                                const wfWithFilteredSubs = hasSubs
                                    ? { ...wf, subActions: visibleSubs }
                                    : { ...wf, subActions: undefined };
                                return (
                                    <Col xxl={3} xl={4} md={6} sm={6} key={wf.key}>
                                        <div style={{ height: "100%" }}>
                                            <MenuCard
                                                icon={wf.icon}
                                                label={wf.label}
                                                count={hasSubs ? visibleSubs.length : undefined}
                                                subtitle={wf.subtitle}
                                                tone={wf.tone}
                                                onClick={() => handleWorkflowClick(wfWithFilteredSubs)}
                                            />
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                );
            })}
        </div>
    );
}
